'use client';

import { useState, useEffect } from 'react';

const CONTENT_SELECTOR = 'article .blog-content';

function slugFromText(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function ensureUniqueIds(ids) {
  const seen = new Map();
  return ids.map((id) => {
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    return count === 0 ? id : `${id}-${count}`;
  });
}

const POST_TITLE_ID = 'post-title';

/**
 * Table of Contents built from optional post title (first item) and H1/H2 in the article.
 * Renders in layout next to content (sticky). Scoped to CONTENT_SELECTOR; adds IDs to headings if missing.
 */
export function TableOfContents({ className = '', title: postTitle = '', lang = 'en' }) {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const container = document.querySelector(CONTENT_SELECTOR);
    const titleItem =
      postTitle && document.getElementById(POST_TITLE_ID)
        ? [{ level: 1, text: postTitle, id: POST_TITLE_ID }]
        : [];

    if (!container) {
      setItems(titleItem);
      return;
    }

    const headings = container.querySelectorAll('h1, h2');
    if (headings.length === 0) {
      setItems(titleItem);
      return;
    }

    const rawItems = [];
    const rawIds = [];

    headings.forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (tag !== 'h1' && tag !== 'h2') return;
      const text = el.textContent?.trim() || '';
      let id = el.id?.trim() || slugFromText(text) || `heading-${rawItems.length}`;
      if (!id) id = `heading-${rawItems.length}`;
      rawIds.push(id);
      el.id = id; // set now; we'll dedupe next
      rawItems.push({ level: tag === 'h1' ? 1 : 2, text, id });
    });

    const uniqueIds = ensureUniqueIds(rawIds);
    headings.forEach((el, i) => {
      if (uniqueIds[i]) el.id = uniqueIds[i];
    });
    const contentItems = rawItems.map((item, i) => ({ ...item, id: uniqueIds[i] || item.id }));

    setItems([...titleItem, ...contentItems]);
  }, [postTitle]);

  // Optional: highlight active section on scroll
  useEffect(() => {
    if (items.length === 0) return;

    const container = document.querySelector(CONTENT_SELECTOR);
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id || '');
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -66% 0px', threshold: 0 }
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className={`hidden xl:block sticky top-28 self-start w-52 min-w-0 max-h-[calc(100vh-8rem)] overflow-y-auto pt-8 pb-3 pl-4 border-l border-gray-200 ${className}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        {lang === 'fi' ? 'Tällä sivulla' : 'On this page'}
      </p>
      <ul className="space-y-1.5 text-sm">
        {items.map(({ text, id }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              aria-current={activeId === id ? 'true' : undefined}
              className={`flex items-start min-h-[2.5rem] py-0.5 text-gray-700 hover:text-brand transition-colors border-l-2 -ml-px pl-2 ${
                activeId === id ? 'border-brand text-brand font-medium' : 'border-transparent'
              }`}
            >
              <span className="line-clamp-2 leading-snug">{text}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
